import { SafeAreaView } from 'react-native';
import AnimatedBottomSheet from '../../../../components/shared/AnimatedBottomSheet/AnimatedBottomSheet';
import React, { useCallback } from 'react';
import { calcHeight, calcWidth, deviceHeight } from '../../../../utils/dimensions';
import styled, { useTheme } from 'styled-components/native';
import { useDispatch, useSelector } from 'react-redux';
import { isFilterBottomSheetOpenSelector } from '../../../../store/analytics/analytics.selector';
import { setFilterBottomSheetOpen } from '../../../../store/analytics/analytics.slice';
import LockIcon from '../../../../assets/icons/analytics/lock.svg';
import CheckmarkIcon from '../../../../assets/icons/analytics/checkmark.svg';
import { displayModeSelector } from '../../../../store/attributes/attributes.selector';
import { log } from '../../../../store/appActivity/appActivity.slice';
import { setDisplayMode } from '../../../../store/attributes/attributes.slice';
import { useTranslation } from 'react-i18next';

const MAX_HEIGHT = deviceHeight * 0.8;
const MIN_HEIGHT = calcHeight(300);
const FilterMenuBottomSheet = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const displayMode = useSelector(displayModeSelector);
  const isFilterBottomSheetOpen = useSelector(isFilterBottomSheetOpenSelector);

  const handleMenuClose = useCallback(() => {
    dispatch(setFilterBottomSheetOpen(false));
  }, [dispatch]);

  const handlePercentsPress = useCallback(() => {
    dispatch(
      log({
        event: 'FilterMenuBottomSheet.handlePercentsPress',
        data: { displayMode },
      }),
    );

    dispatch(setDisplayMode('percentage'));
    handleMenuClose();
  }, [dispatch, displayMode, handleMenuClose]);

  const handleAbsolutePress = useCallback(() => {
    dispatch(
      log({
        event: 'FilterMenuBottomSheet.handleAbsolutePress',
        data: { displayMode },
      }),
    );

    dispatch(setDisplayMode('absolute'));
    handleMenuClose();
  }, [dispatch, displayMode, handleMenuClose]);

  return (
    <AnimatedBottomSheet
      isVisible={isFilterBottomSheetOpen}
      onClose={handleMenuClose}
      maxHeight={MAX_HEIGHT}
      minHeight={MIN_HEIGHT}
      backdropOpacity={0.5}
    >
      <SafeAreaView>
        <S.ContentContainer>
          <S.List>
            <FilterMenuListItem
              type={EFilterMenuListItemType.CHECKMARK}
              text={t('analytics.filter.sharedWith')}
              isLocked
              isInactive
            />

            <S.Divider />

            <FilterMenuListItem
              type={EFilterMenuListItemType.CHECKMARK}
              text={t('analytics.filter.activeUsers')}
              isSelected
              isInactive
            />
            <FilterMenuListItem
              type={EFilterMenuListItemType.CHECKMARK}
              text={t('analytics.filter.inactiveUsers')}
              isInactive
            />
            <FilterMenuListItem
              type={EFilterMenuListItemType.CHECKMARK}
              text={t('analytics.filter.guestsUsers')}
              isSelected
              isInactive
            />

            <S.Divider />

            <FilterMenuListItem
              onPress={handlePercentsPress}
              type={EFilterMenuListItemType.POINTER}
              isSelected={displayMode === 'percentage'}
              text={t('analytics.filter.percents')}
            />
            <FilterMenuListItem
              onPress={handleAbsolutePress}
              type={EFilterMenuListItemType.POINTER}
              isSelected={displayMode === 'absolute'}
              text={t('analytics.filter.numbers')}
            />

            <S.Divider />

            <FilterMenuListItem
              type={EFilterMenuListItemType.POINTER}
              text={t('analytics.filter.allTimeViews')}
              isSelected
              isInactive
            />
            <FilterMenuListItem
              type={EFilterMenuListItemType.POINTER}
              text={t('analytics.filter.lastSevenDaysViews')}
              isInactive
            />
            <FilterMenuListItem
              type={EFilterMenuListItemType.POINTER}
              text={t('analytics.filter.lastThirtyDaysViews')}
              isInactive
            />
            <FilterMenuListItem
              type={EFilterMenuListItemType.POINTER}
              text={t('analytics.filter.thisYearViews')}
              isInactive
            />
          </S.List>
        </S.ContentContainer>
      </SafeAreaView>
    </AnimatedBottomSheet>
  );
};

enum EFilterMenuListItemType {
  CHECKMARK = 'checkmark',
  POINTER = 'pointer',
}

interface IFilterMenuListItemProps {
  onPress?: () => void;
  text: string;
  type: EFilterMenuListItemType;
  isSelected?: boolean;
  isLocked?: boolean;
  isInactive?: boolean;
}

const FilterMenuListItem: React.FC<IFilterMenuListItemProps> = ({
  onPress,
  text,
  type,
  isSelected,
  isLocked,
  isInactive,
}) => {
  const theme = useTheme();
  const iconColor = isInactive ? theme.colors.darkGray3 : theme.colors.primaryBlue;

  return (
    <S.ListItem disabled={isInactive} onPress={onPress}>
      <S.ListItemTextContainer>
        <S.ListItemText isInactive={isInactive}>{text}</S.ListItemText>
        {isLocked && (
          <S.LockIconContainer>
            <LockIcon />
          </S.LockIconContainer>
        )}
      </S.ListItemTextContainer>
      {isSelected &&
        (type === EFilterMenuListItemType.CHECKMARK ? (
          <CheckmarkIcon color={iconColor} />
        ) : (
          <S.Point color={iconColor} />
        ))}
    </S.ListItem>
  );
};

const S = {
  ContentContainer: styled.View`
    height: auto;
    width: 100%;
    padding: ${calcHeight(25)}px ${calcWidth(25)}px;
  `,
  List: styled.View`
    align-items: center;
  `,
  ListItem: styled.TouchableOpacity`
    width: 100%;
    padding: ${calcHeight(10)}px 0;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  `,
  ListItemText: styled.Text<{ isInactive: boolean }>`
    font-size: ${({ theme }) => theme.fontSizes.s16};
    color: ${({ theme, isInactive }) => (isInactive ? theme.colors.darkGray3 : theme.colors.text)};
  `,
  Divider: styled.View`
    width: 100%;
    height: 1px;
    background-color: ${({ theme }) => theme.colors.overviewDivider};
    margin: ${calcHeight(15)}px 0;
  `,
  ListItemTextContainer: styled.View`
    flex-direction: row;
    align-items: center;
  `,
  Point: styled.View<{ color: string }>`
    width: ${calcWidth(8)}px;
    height: ${calcWidth(8)}px;
    border-radius: ${calcWidth(4)}px;
    background-color: ${({ color }) => color};
  `,
  LockIconContainer: styled.View`
    margin-left: ${calcWidth(10)}px;
  `,
};

export default FilterMenuBottomSheet;
